import { CapterraOriginal, G2Original, GoogleOriginal, TrustpilotOriginal } from '@brightlocal/icons';
import { BASE_FLOW_SUBSCRIPTION_ID, SIMPLY_LISTINGS_SUBSCRIPTION_ID } from '@brightlocal/shared';

import { TERMS_AND_CONDITIONS_URL } from 'shared/consts';
import { PACKAGE_FLOWS, type PackageFlow } from 'shared/utils';

export const PRIVACY_POLICY_URL = 'https://www.brightlocal.com/privacy-policy/';
/** Re-exported from shared consts (also used by the Citation Builder checkout) so the T&C URL has one source. */
export const TERMS_OF_SERVICE_URL = TERMS_AND_CONDITIONS_URL;

/** Subscription IDs that are accepted by the sign-up route. */
export const ALLOWED_SUBSCRIPTION_IDS = [BASE_FLOW_SUBSCRIPTION_ID, SIMPLY_LISTINGS_SUBSCRIPTION_ID] as const;

/** Subscription ID accepted by the sign-up route. */
export type SignUpSubscriptionId = (typeof ALLOWED_SUBSCRIPTION_IDS)[number];

/** Type guard verifying whether the given value is a subscription ID accepted by the sign-up route. */
export const isSignUpSubscriptionId = (id: string): id is SignUpSubscriptionId =>
  ALLOWED_SUBSCRIPTION_IDS.includes(id as SignUpSubscriptionId);

/** Review-platform ratings shown on the sign-up branding panel. */
export const REVIEW_PLATFORMS = [
  { key: 'google', Icon: GoogleOriginal, rating: 4.8, reviewCount: '366' },
  { key: 'g2', Icon: G2Original, rating: 4.6, reviewCount: '228' },
  { key: 'capterra', Icon: CapterraOriginal, rating: 4.8, reviewCount: '281' },
  { key: 'trustpilot', Icon: TrustpilotOriginal, rating: 4.6, reviewCount: '752' }
] as const;

export const SIGN_UP_FIELDS = {
  EMAIL: 'userEmail',
  PASSWORD: 'userPassword',
  MARKETING_OPT_IN: 'userMarketingOptIn',
  PRIVACY_OPT_IN: 'customerPrivacyOptIn',
  RECAPTCHA: 'gRecaptchaResponse'
} as const;

export const VALIDATION_ERROR_CODES = {
  EMAIL_REQUIRED: 'EMAIL_REQUIRED',
  EMAIL_TOO_LONG: 'EMAIL_TOO_LONG',
  EMAIL_INVALID_FORMAT: 'EMAIL_INVALID_FORMAT',
  PASSWORD_REQUIRED: 'PASSWORD_REQUIRED',
  PASSWORD_TOO_SHORT: 'PASSWORD_TOO_SHORT',
  PASSWORD_TOO_LONG: 'PASSWORD_TOO_LONG',
  PASSWORD_REPEATED_CHAR: 'PASSWORD_REPEATED_CHAR',
  RECAPTCHA_REQUIRED: 'RECAPTCHA_REQUIRED'
} as const;

/**
 * Maps a sign-up subscription ID to its GTM `eventAction` for sign-up tracking events.
 */
export const SIGN_UP_FLOW_BY_SUBSCRIPTION: Record<SignUpSubscriptionId, PackageFlow> = {
  [BASE_FLOW_SUBSCRIPTION_ID]: PACKAGE_FLOWS.BASIC,
  [SIMPLY_LISTINGS_SUBSCRIPTION_ID]: PACKAGE_FLOWS.CB_ONLY
};

/** Static tracking configuration for the email/password sign-up form. Pair with the active flow as `eventAction`. */
export const SIGN_UP_FORM_TRACKING_CONFIG = {
  eventPrefix: 'signup',
  eventCategory: 'onboarding_journey',
  eventLabel: 'email'
} as const;

/** Static tracking configuration for the sign-up page Google OAuth flow. Pair with the active flow as `eventAction`. */
export const SIGN_UP_TRACKING_CONFIG = {
  eventPrefix: 'signup',
  eventCategory: 'onboarding_journey'
} as const;

/** Action for the sign-up form reCAPTCHA token. */
export const SIGNUP_RECAPTCHA_ACTION = 'signup' as const;
